import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Text,
  VStack,
  Alert,
  Flex,
  Icon,
} from "@chakra-ui/react";
import { Field, Formik, Form } from "formik";
import { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateUserRequest } from "store/user/reducer";
import { userSelector } from "store/user/selector";
import { alertSelector } from "store/ui/selector";
import * as Yup from "yup";
import { AuthInput } from "components/auth/AuthInput";
import { ErrorAlert } from "views/auth/signIn/components/ErrorAlert";

type PersonalInfoInputs = {
  first_name: string;
  last_name: string;
  phone: string;
  email: string;
};

const personalInfoSchema = Yup.object({
  first_name: Yup.string().required("First Name is required."),
  last_name: Yup.string().required("Last Name is required."),
  phone: Yup.string(),
  email: Yup.string().email("Invalid email address").required("Email Address is required."),
});

const buttonStyle = {
  bg: "#3B82F6",
  color: "#FFF",
  fontSize: "14px",
  fontWeight: "600",
  padding: "12px 24px",
  height: "40px",
  borderRadius: "24px",
  border: "1px solid #3B82F6",
  _hover: {
    bg: "#FFF",
    color: "#3B82F6",
  },
  _active: {
    bg: "#1D4ED8",
  },
};

export const SuccessCheckIcon = (props: any) => (
  <Icon viewBox="0 0 48 48" {...props}>
    <circle cx="24" cy="24" r="18" fill="none" stroke="#22C55E" strokeWidth="4" />
    <path
      d="M17 24.5l5 5 11-12"
      fill="none"
      stroke="#22C55E"
      strokeWidth="4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Icon>
);

export default function PersonalInformationCard() {
  const dispatch = useDispatch();
  const user = useSelector(userSelector);
  const alert = useSelector(alertSelector);
  const [personalInfoSuccess, setPersonalInfoSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [shouldShowError, setShouldShowError] = useState(false);

  const personalInfoInitialValues: PersonalInfoInputs = {
    first_name: user?.first_name || "",
    last_name: user?.last_name || "",
    phone: user?.phone || "",
    email: user?.email || "",
  };

  const onUpdatePersonalInfo = async (values: PersonalInfoInputs) => {
    setIsLoading(true);
    setShouldShowError(true);
    try {
      await dispatch(
        updateUserRequest({
          user_id: user.user_id,
          user_details: values,
        })
      );
      setPersonalInfoSuccess(true);
      setTimeout(() => setPersonalInfoSuccess(false), 5000);
    } catch (error) {
      // Handle error if needed
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      bg="white"
      borderRadius="12px"
      border="1px solid #E5E7EB"
      p="22px"
      mb="24px"
      maxW="691px"
    >
      <Text fontSize="16px" fontWeight="600" color="#0B0E10" mb="4px">
        Personal information
      </Text>
      <Text fontSize="14px" color="#0B0E10" mb="24px">
        This is where we'll contact you. Make sure this stays up to date so we can communicate important information about your translation orders to you.
      </Text>

      {shouldShowError && alert?.type === "error" && !!alert?.message && (
        <ErrorAlert icon message={alert.message} onClose={() => setShouldShowError(false)} />
      )}

      {personalInfoSuccess && (
        <Alert status="success" bg="#E9F7EF" mb="24px" borderRadius="8px">
          <SuccessCheckIcon ml="auto" mr="10px" boxSize="28px" />
          <Text flex="1">Your personal info has been updated successfully.</Text>
        </Alert>
      )}

      {/* Inner card for form fields */}
      <Box
        bg="transparent"
        borderRadius="8px"
        border="1px solid #D2DAE5"
        p="16px"
      >
        <Formik
          initialValues={personalInfoInitialValues}
          validationSchema={personalInfoSchema}
          validateOnBlur={false}
          validateOnChange={false}
          onSubmit={onUpdatePersonalInfo}
        >
          {({ errors, submitCount }) => (
            <Form>
              <VStack spacing="16px" align="stretch">
                {/* First Name and Last Name in the same row */}
                <Flex
                  direction={{ base: "column", lg: "row" }}
                  gap={{ base: "20px", lg: "24px" }}
                  align="stretch"
                >
                  <FormControl isInvalid={submitCount > 0 && !!errors.first_name}>
                    <FormLabel fontSize="14px" fontWeight="400" color="#0B0E10" mb="6px">
                      First Name
                    </FormLabel>
                    <Field name="first_name">
                      {({ field }: any) => (
                        <AuthInput
                          {...field}
                          role="customer"
                          chakraVariant="authCustomerNew"
                          placeholder="Enter your first name"
                          isInvalid={submitCount > 0 && !!errors.first_name}
                        />
                      )}
                    </Field>
                  </FormControl>

                  <FormControl isInvalid={submitCount > 0 && !!errors.last_name}>
                    <FormLabel fontSize="14px" fontWeight="400" color="#0B0E10" mb="6px">
                      Last Name
                    </FormLabel>
                    <Field name="last_name">
                      {({ field }: any) => (
                        <AuthInput
                          {...field}
                          role="customer"
                          chakraVariant="authCustomerNew"
                          placeholder="Enter your last name"
                          isInvalid={submitCount > 0 && !!errors.last_name}
                        />
                      )}
                    </Field>
                  </FormControl>
                </Flex>

                <FormControl isInvalid={submitCount > 0 && !!errors.phone}>
                  <FormLabel fontSize="14px" fontWeight="400" color="#0B0E10" mb="6px">
                    Phone Number
                  </FormLabel>
                  <Field name="phone">
                    {({ field }: any) => (
                      <AuthInput
                        {...field}
                        role="customer"
                        chakraVariant="authCustomerNew"
                        placeholder="Enter your phone number"
                        isInvalid={submitCount > 0 && !!errors.phone}
                      />
                    )}
                  </Field>
                </FormControl>

                <FormControl isInvalid={submitCount > 0 && !!errors.email}>
                  <FormLabel fontSize="14px" fontWeight="400" color="#0B0E10" mb="6px">
                    Email Address
                  </FormLabel>
                  <Field name="email">
                    {({ field }: any) => (
                      <AuthInput
                        {...field}
                        role="customer"
                        chakraVariant="authCustomerNew"
                        placeholder="Enter your email address"
                        isInvalid={submitCount > 0 && !!errors.email}
                      />
                    )}
                  </Field>
                </FormControl>

                <Button type="submit"  w="192px" h="40px" sx={buttonStyle} isLoading={isLoading}>
                  Save changes
                </Button>
              </VStack>
            </Form>
          )}
        </Formik>
      </Box>
    </Box>
  );
}
